/*
 * vld-design.js - draws a VickersLDN factory design (design.json, format vickersldn-design/1) as live SVG.
 *
 * The same file runs in the hub Studio and on the Shopify product page. Text stays text (any open font from
 * fonts.json, any words), art stays vector where the original was vector. Text is fitted into the box the
 * designer drew, like the server renderer (services/design_render.py); arcs use SVG textPath, rotation and the
 * outline stroke are native SVG.
 *
 *   const d = await VLD.load(baseUrl)                 // baseUrl = folder holding design.json (+ layers/)
 *   await VLD.render(svgElement, d, {layers: {l4: {text: "Nanny", font: "shalimar"}}}, {fontsCss: url})
 *   VLD.fit(...) reports per-layer {scale, tooLong} so the page can warn instead of shrinking text to nothing.
 */
(function (root) {
  const NS = "http://www.w3.org/2000/svg";
  const XL = "http://www.w3.org/1999/xlink";
  const MIN_SCALE = 0.45; // below this the text is flagged tooLong (warn the customer, do not silently shrink)
  let fontCatalogue = null;

  function el(name, attrs, parent) {
    const e = document.createElementNS(NS, name);
    for (const k in attrs || {}) if (attrs[k] !== undefined && attrs[k] !== null) e.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(e);
    return e;
  }

  async function load(baseUrl) {
    const url = baseUrl.replace(/\/$/, "") + "/design.json";
    const r = await fetch(url);
    if (!r.ok) throw new Error("design.json " + r.status);
    const d = await r.json();
    d._base = baseUrl.replace(/\/$/, "");
    return d;
  }

  async function fonts(fontsJsonUrl) {
    if (fontCatalogue) return fontCatalogue;
    fontCatalogue = await (await fetch(fontsJsonUrl)).json();
    return fontCatalogue;
  }

  function resolveFont(spec) {
    const cat = fontCatalogue || {fonts: {}, psd_map: {}};
    spec = (cat.psd_map && cat.psd_map[spec]) || spec || "great-vibes";
    const [id, w] = String(spec).split(":");
    const f = (cat.fonts || {})[id];
    return {family: "vld-" + id, weight: w ? +w : (f && f.weight) || 400};
  }

  function linesOf(text, layer, ov) {
    const per = ov.font ? [] : (layer.lines || []);
    const baseCase = ov.case || layer.case;
    return String(text).replace(/\r/g, "\n").split("\n").map((t, i) => {
      const s = per[i] || {};
      const f = resolveFont(ov.font || s.font || layer.font);
      if ((s.case || baseCase) === "upper") t = t.toUpperCase();
      return {text: t, family: f.family, weight: ov.weight || f.weight, rel: s.rel_size || 1};
    });
  }

  const _ctx = (typeof document !== "undefined") ? document.createElement("canvas").getContext("2d") : null;

  function inkOf(lines, size, align, tracking, leading) {
    // ink box of a text block in its own coordinates (baseline of line 1 at y=0), like the server's crop-to-ink
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, y = 0;
    _ctx.textAlign = {left: "left", right: "right"}[align] || "center";
    lines.forEach((ln, i) => {
      const sz = size * ln.rel;
      if (i > 0) y += sz * 1.2 * leading;
      if (!ln.text.trim()) return;
      _ctx.font = `${ln.weight} ${sz}px "${ln.family}"`;
      if ("letterSpacing" in _ctx) _ctx.letterSpacing = tracking ? `${sz * tracking / 1000}px` : "0px";
      const m = _ctx.measureText(ln.text);
      x0 = Math.min(x0, -m.actualBoundingBoxLeft); x1 = Math.max(x1, m.actualBoundingBoxRight);
      y0 = Math.min(y0, y - m.actualBoundingBoxAscent); y1 = Math.max(y1, y + m.actualBoundingBoxDescent);
    });
    if (!isFinite(x0)) return {x: 0, y: 0, width: 1, height: 1};
    return {x: x0, y: y0, width: x1 - x0, height: y1 - y0};
  }

  function textWidth(line, size, tracking) {
    _ctx.font = `${line.weight} ${size}px "${line.family}"`;
    if ("letterSpacing" in _ctx) _ctx.letterSpacing = tracking ? `${size * tracking / 1000}px` : "0px";
    return _ctx.measureText(line.text).width;
  }

  function textBlock(g, lines, size, align, tracking, leading, colour) {
    const t = el("text", {fill: colour, "text-anchor": {left: "start", right: "end"}[align] || "middle",
      "font-kerning": "normal", style: "white-space:pre"}, g);
    let y = 0;
    lines.forEach((ln, i) => {
      const sz = size * ln.rel;
      if (i > 0) y += sz * 1.2 * leading;
      const ts = el("tspan", {x: 0, y: y, "font-family": ln.family, "font-weight": ln.weight, "font-size": sz,
        "letter-spacing": tracking ? (sz * tracking / 1000) : null}, t);
      ts.textContent = ln.text || " ";
    });
    return t;
  }

  function arcText(g, line, size, bend, boxW, tracking, colour, id) {
    // Arc warp like Photoshop's: bend 100 = half circle; the arc length equals the text's length.
    const theta = Math.max(Math.abs(bend) / 100 * Math.PI, 0.05);
    const R = textWidth(line, size, tracking) / theta;
    const half = theta / 2;
    const x0 = -R * Math.sin(half), x1 = R * Math.sin(half);
    const up = bend > 0;
    const yEdge = up ? R * (1 - Math.cos(half)) : -R * (1 - Math.cos(half));
    const d = up ? `M ${x0} ${yEdge} A ${R} ${R} 0 0 1 ${x1} ${yEdge}` : `M ${x0} ${yEdge} A ${R} ${R} 0 0 0 ${x1} ${yEdge}`;
    el("path", {id: id, d: d, fill: "none"}, g);
    const t = el("text", {fill: colour, "font-family": line.family, "font-weight": line.weight, "font-size": size,
      "letter-spacing": tracking ? size * tracking / 1000 : null, style: "white-space:pre"}, g);
    const tp = el("textPath", {href: "#" + id, startOffset: "50%", "text-anchor": "middle"}, t);
    tp.setAttributeNS(XL, "xlink:href", "#" + id);
    tp.textContent = line.text;
    return t;
  }

  function placeImage(svg, layer, base) {
    const [x0, y0, x1, y1] = layer.box;
    const href = base + "/" + (layer.svg || layer.src);
    if (layer.svg && layer.quad) {
      const [tl, tr, , bl] = layer.quad;
      // the vector source is drawn into a unit square, then mapped onto the quad (affine: tl, tr, bl)
      const g = el("g", {transform: `matrix(${tr[0] - tl[0]} ${tr[1] - tl[1]} ${bl[0] - tl[0]} ${bl[1] - tl[1]} ${tl[0]} ${tl[1]})`,
        opacity: layer.opacity < 1 ? layer.opacity : null}, svg);
      const im = el("image", {x: 0, y: 0, width: 1, height: 1, preserveAspectRatio: "none", href: href,
        "data-fallback": layer.src ? base + "/" + layer.src : null}, g);
      im.setAttributeNS(XL, "xlink:href", href);
      // a vector that fails to load (missing file, blocked response) falls back to the layer's PNG
      im.addEventListener("error", () => {
        const fb = im.getAttribute("data-fallback");
        if (fb && im.getAttribute("href") !== fb) { im.setAttribute("href", fb); im.setAttributeNS(XL, "xlink:href", fb); }
      }, {once: true});
      return im;
    }
    const im = el("image", {x: x0, y: y0, width: x1 - x0, height: y1 - y0, preserveAspectRatio: "none", href: href,
      opacity: layer.opacity < 1 ? layer.opacity : null}, svg);
    im.setAttributeNS(XL, "xlink:href", href);
    return im;
  }

  function fitInto(g, node, box, align, rotate, ink) {
    // measured at size 100: scale the group so the ink fits the box (contain) and align it
    const bb = ink || node.getBBox();
    const [x0, y0, x1, y1] = box;
    const bw = x1 - x0, bh = y1 - y0;
    let w = bb.width, h = bb.height;
    const rad = (rotate || 0) * Math.PI / 180;
    if (rotate) {
      const c = Math.abs(Math.cos(rad)), s = Math.abs(Math.sin(rad));
      [w, h] = [w * c + h * s, w * s + h * c];
    }
    const k = Math.min(bw / Math.max(w, 1), bh / Math.max(h, 1));
    const cx = bb.x + bb.width / 2, cy = bb.y + bb.height / 2;
    let tx = x0 + bw / 2;
    if (!rotate && align === "left") tx = x0 + (bb.width * k) / 2;
    if (!rotate && align === "right") tx = x1 - (bb.width * k) / 2;
    const ty = y0 + bh / 2;
    g.setAttribute("transform", `translate(${tx} ${ty}) rotate(${rotate || 0}) scale(${k}) translate(${-cx} ${-cy})`);
    return k;
  }

  async function render(svg, design, overrides, opts) {
    opts = opts || {};
    if (opts.fontsJson) await fonts(opts.fontsJson);
    const ov = (overrides && overrides.layers) || {};
    const [W, H] = design.canvas;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    svg.setAttribute("xmlns", NS);
    if (opts.background) el("rect", {x: 0, y: 0, width: W, height: H, fill: opts.background}, svg);
    const base = design._base || opts.base || "";
    const report = {};
    const pending = [];
    for (const L of design.layers) {
      const o = ov[L.id] || {};
      if (o.hidden || L.hidden) continue;
      if (L.kind === "image") {
        // art sits in its own layer group too, so the editor can drag / resize / hide it like a text layer
        const ga = el("g", {"data-layer": L.id, "data-kind": "image"}, svg);
        placeImage(ga, L, base);
        continue;
      }
      if (L.kind !== "text") continue;
      const text = o.text !== undefined ? o.text : L.text;
      if (!String(text).trim()) continue;
      const lines = linesOf(text, L, o);
      const g = el("g", {"data-layer": L.id}, svg);
      const colour = o.colour || L.colour || "#111111";
      const warp = o.warp || L.warp;
      const rot = o.rotate !== undefined ? o.rotate : (L.rotate || 0);
      const tracking = o.font ? 0 : (L.tracking || 0);
      let node, ink = null;
      const align = o.align || L.align || "center";
      if (warp && warp.style === "arc" && lines.length === 1) {
        node = arcText(g, lines[0], 100, warp.bend, (L.box[2] - L.box[0]) / 1, tracking, colour, `arc-${L.id}`);
      } else {
        node = textBlock(g, lines, 100, align, tracking, L.leading || 1, colour);
        ink = () => inkOf(lines, 100, align, tracking, L.leading || 1);
      }
      const st = L.stroke;
      pending.push(() => {
        const k = fitInto(g, node, L.box, align, rot, ink ? ink() : null);
        // natural size: the designer's point size; k * 100 is the size we ended at
        const natural = L.size || 100;
        report[L.id] = {scale: k, size: k * 100, tooLong: (k * 100) / natural < MIN_SCALE};
        if (st && st.size) {  // outline (e.g. white ring lifting a name off busy art), in canvas pixels
          node.setAttribute("stroke", st.colour); node.setAttribute("paint-order", "stroke");
          node.setAttribute("stroke-linejoin", "round"); node.setAttribute("stroke-width", 2 * st.size / k);
        }
      });
    }
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    pending.forEach(f => f());
    return report;
  }

  function serialize(svg) {
    return new XMLSerializer().serializeToString(svg);
  }

  root.VLD = {load, fonts, render, serialize, resolveFont, MIN_SCALE};
})(typeof window !== "undefined" ? window : this);
